import { omit } from "lodash";
import { ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ReactComponent as LogoSVG } from "../../assets/sekuritance-logo.svg";
import { colours } from "../../styles/colours";
import { Icon } from "../icons";
import { Flex } from "../layout";
import { WhiteItem } from "../menu/item/white";

export const animationDelay = 300;

namespace Logo {
  export const Text = styled.div`
    margin-left: 0.3em;
    font-size: 2.1em;
    font-weight: 700;
  `;

  export const Wrapper = styled.div`
    padding: 2em;
    margin-bottom: 1em;
    border-bottom: 2px solid ${colours.darkWhite};
    position: relative;
  `;

  export const Minimize = styled(Flex).attrs({
    center: true,
    as: "button",
  })`
    width: 2em;
    height: 2em;
    border: 2px solid ${colours.darkWhite};
    border-radius: 50%;
    background: ${colours.primaryBlue};
    position: absolute;
    right: -1em;
    bottom: -1em;
    font-size: 0.8em;

    cursor: pointer;
    &:hover {
      width: 2.5em;
      height: 2.5em;
      right: -1.25em;
      bottom: -1.25em;
    }
  `;
}

const DeveloperItem = styled(WhiteItem)`
  margin-top: 1em;
  border-top: 1px solid ${colours.grey};
`;

const DeveloperLink = styled.a.attrs({
  href: "https://sekuritance.readme.io/",
})`
  display: block;
`;

const Container = styled.nav<{ collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  transition: width ${animationDelay / 1000}s ease-in-out;
  height: 100%;
  background: ${colours.white};
  flex-shrink: 0;
  font-weight: 700;
  font-family: "Helvetica Neue", sans-serif;
  ${(props) => (props.collapsed ? "width: 6em" : "width: 20em")};
`;

const MenuWrapper = styled(Flex)`
  flex-grow: 1;
  flex-shrink: 1;
  overflow: auto;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
`;

interface SidebarBaseInternalProps {
  children?: React.ReactNode;
  delayedCollapsed: boolean;
  immediateCollapsed: boolean;
  toggleCollapsed: () => void;
}

export type SidebarBasePropTypes = ComponentProps<typeof Container> &
  SidebarBaseInternalProps;

export const SidebarBase: React.FunctionComponent<SidebarBasePropTypes> = (
  props
) => {
  const { t } = useTranslation("common");
  const propsToPass = omit(
    props,
    "children",
    "delayedCollapsed",
    "immediateCollapsed",
    "toggleCollapsed"
  );

  return (
    <Container {...propsToPass} collapsed={props.immediateCollapsed}>
      <Logo.Wrapper>
        <Flex>
          <LogoSVG />
          {props.delayedCollapsed ? null : (
            <Logo.Text className="h2">Sekuritance</Logo.Text>
          )}
          <Logo.Minimize onClick={() => props.toggleCollapsed()}>
            <Icon
              type="arrowDown"
              colour1={colours.white}
              scale={0.8}
              rotateAmount={props.immediateCollapsed ? 270 : 90}
            />
          </Logo.Minimize>
        </Flex>
      </Logo.Wrapper>
      <MenuWrapper>
        {props.children}
        <DeveloperLink target="_blank" rel="noreferrer">
          <DeveloperItem compact={props.delayedCollapsed} icon="api">
            {t("developers")}
          </DeveloperItem>
        </DeveloperLink>
      </MenuWrapper>
    </Container>
  );
};
